/**
 * hi-product.js — comportamento dos blocos `hi-*` da página de produto.
 *
 * <hi-live-block>
 *   Bloco cujo conteúdo depende da variante (preço, PIX, parcelas, kits).
 *   Quando o cliente troca de variante, a Pitch dispara `productSelect` na
 *   seção com o HTML novo; cada bloco copia o próprio trecho, achado pelo
 *   `data-block-id` — mesmo padrão do <product-price> nativo.
 *
 * <hi-kit-selector> (estende hi-live-block)
 *   Cards de 1/2/3 unidades. A escolha só define a QUANTIDADE que vai para o
 *   carrinho; a economia é aplicada por desconto automático do admin.
 *   Escreve no seletor de quantidade nativo, se existir no bloco de compra,
 *   ou num <input name="quantity"> próprio ligado ao formulário via `form=`.
 */
import { StandardEvents } from '@shopify/events';

class HiLiveBlock extends HTMLElement {
  #section = null;

  connectedCallback() {
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        const html = detail?.html;
        if (!html) return;
        const fresh = html.querySelector(`[data-hi-block="${this.dataset.hiBlock}"] [data-hi-swap]`);
        const current = this.querySelector('[data-hi-swap]');
        if (fresh && current) current.replaceWith(document.importNode(fresh, true));
        this.afterSwap();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[hi-live-block]', error);
      });
  };

  /** Ganchos para subclasses. */
  afterSwap() {}
}

class HiKitSelector extends HiLiveBlock {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#onChange);
    // Aplica a opção marcada no carregamento (ex.: kit em destaque pré-selecionado).
    // setTimeout (e não requestAnimationFrame): roda mesmo com a aba em segundo plano.
    setTimeout(() => this.#apply(), 0);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#onChange);
  }

  afterSwap() {
    // O HTML novo volta com o padrão marcado; restaura a escolha do cliente.
    const input = this.querySelector(`input[type="radio"][value="${this.dataset.selected}"]`);
    if (input) input.checked = true;
    this.#apply();
  }

  #onChange = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.type === 'radio') this.#apply();
  };

  #apply() {
    const checked = /** @type {HTMLInputElement|null} */ (this.querySelector('input[type="radio"]:checked'));
    if (!checked) return;
    const quantity = checked.value;
    this.dataset.selected = quantity;

    const form = this.#productForm();
    if (!form) return;

    // 1) Seletor de quantidade nativo, se o lojista manteve o bloco "Quantidade".
    //    O valor vai direto no input (o componente pode ainda não ter carregado
    //    — sem isso o carrinho recebia 1 unidade com o kit de 2 marcado).
    const nativeSelector = form.querySelector('quantity-selector-component');
    const nativeInput = /** @type {HTMLInputElement|null} */ (
      form.querySelector('input[name="quantity"]:not([data-hi-quantity])')
    );
    if (nativeInput) nativeInput.value = quantity;
    if (nativeSelector) {
      if (typeof nativeSelector.setValue === 'function') {
        nativeSelector.setValue(quantity);
        nativeSelector.refs?.quantityInput?.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        customElements.whenDefined('quantity-selector-component').then(() => this.#apply());
      }
    }

    // 2) Input próprio (sempre presente) — garante que o POST /cart/add leve a quantidade.
    const hidden = /** @type {HTMLInputElement|null} */ (this.querySelector('input[data-hi-quantity]'));
    if (hidden) {
      hidden.value = quantity;
      hidden.disabled = Boolean(nativeSelector || nativeInput);
      const formEl = form.querySelector('form[data-type="add-to-cart-form"]');
      if (formEl?.id) hidden.setAttribute('form', formEl.id);
    }

    // 3) Fallback usado pelo product-form para o evento de carrinho.
    const component = form.closest('product-form-component') ?? form;
    if (component instanceof HTMLElement) component.dataset.quantityDefault = quantity;
  }

  #productForm() {
    const scope = this.closest('.shopify-section, dialog') ?? document;
    return scope.querySelector('product-form-component');
  }
}

if (!customElements.get('hi-live-block')) customElements.define('hi-live-block', HiLiveBlock);
if (!customElements.get('hi-kit-selector')) customElements.define('hi-kit-selector', HiKitSelector);
